import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientService from '../../../services/patientService';
import DiagnosisService from '../../../services/diagnosisService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Odontogram from './Odontogram';
import { getSurfaceName } from '../../../utils/dentalSurfaceUtils';
import './NewDentalNote.css';
import './DiagnosisStyles.css';

const ViewDischargeOdontogram = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [odontogramData, setOdontogramData] = useState(null);
  const [mode, setMode] = useState('adult');
  const [addedSelections, setAddedSelections] = useState([]);

  const { dischargeEntry, odontogramType } = location.state || {};

  useEffect(() => {
    const loadPatient = async () => {
      if (patientId && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(patientId, auth.user.access_token);
          setPatient(patientData);
        } catch (error) {
          console.error('Error loading patient:', error);
        }
      }
    };
    loadPatient();
  }, [patientId, auth.user?.access_token]);

  useEffect(() => {
    const loadOdontogramData = async () => {
      if (!dischargeEntry || !odontogramType) {
        navigate(`/patients/${patientId}/dental-notes`);
        return;
      }

      try {
        setLoading(true);
        const data = odontogramType === 'initial'
          ? dischargeEntry.initial
          : dischargeEntry.evolution;

        setOdontogramData(data);
        setMode(dischargeEntry.mode || 'adult');
        await convertAndLoadOdontogramData(data);
      } catch (error) {
        console.error('Error loading odontogram data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOdontogramData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dischargeEntry, odontogramType, patientId, navigate]);

  const convertAndLoadOdontogramData = async (data) => {
    if (!data || !data.subNotes || data.subNotes.length === 0) {
      return;
    }

    const token = auth.user.access_token;
    const loadedSelections = [];

    try {
      const diagnosesById = await DiagnosisService.getDiagnosesMap(token).catch(error => {
        console.error('Error loading diagnoses:', error);
        return new Map();
      });

      data.subNotes.forEach((subNote, index) => {
        const diagnosis = diagnosesById.get(subNote.diagnosisId) || {
          id: subNote.diagnosisId,
          name: 'Diagnóstico no encontrado',
          color: '#gray',
          isCompleteTooth: false
        };
        const convertedSelections = {};
        if (subNote.selections && Array.isArray(subNote.selections)) {
          subNote.selections.forEach(selection => {
            const toothLabel = selection.tooth;
            const surface = denormalizeSurface(selection.surface);
            const colorId = selection.colorId || selection.color || diagnosis.color;
            if (!convertedSelections[toothLabel]) {
              convertedSelections[toothLabel] = {};
            }
            convertedSelections[toothLabel][surface] = colorId;
          });
        }
        loadedSelections.push({
          id: Date.now() + index,
          diagnosis: diagnosis,
          selections: convertedSelections,
          noteText: subNote.text || ''
        });
      });

      setAddedSelections(loadedSelections);
    } catch (error) {
      console.error('Error loading odontogram data:', error);
    }
  };

  const denormalizeSurface = (s) => {
    const map = {
      'oclusal/incisal': 'top',
      'distal': 'right',
      'lingual/palatino': 'bottom',
      'mesial': 'left',
      'centro': 'center',
    };
    return map[s] || s;
  };

  const goBack = () => {
    navigate(`/patients/${patientId}/dental-notes`, {
      state: { activeTab: 'discharge' }
    });
  };

  // Merge all selections for odontogram display
  const getAllSelections = () => {
    const combined = {};

    addedSelections.forEach(selection => {
      Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
        if (!combined[toothLabel]) {
          combined[toothLabel] = {};
        }
        Object.entries(surfaceData).forEach(([surface, color]) => {
          combined[toothLabel][surface] = color;
        });
      });
    });

    return combined;
  };

  const getDiagnosisMap = () => {
    const toothSurfaceToDiagnosis = {};

    addedSelections.forEach(selection => {
      if (selection.diagnosis) {
        Object.entries(selection.selections).forEach(([toothLabel, surfaceData]) => {
          Object.entries(surfaceData).forEach(([surface, color]) => {
            const key = `${toothLabel}:${surface}`;
            toothSurfaceToDiagnosis[key] = selection.diagnosis;
          });
        });
      }
    });

    return toothSurfaceToDiagnosis;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="new-dental-note-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando odontograma...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="new-dental-note-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle={`${odontogramType === 'initial' ? 'Odontograma Inicial' : 'Odontograma Evolutivo'} - Alta del ${dischargeEntry ? formatDate(dischargeEntry.date) : ''}`}
        onBackClick={goBack}
      />

      <div className="odontogram-page-content">
        {/* Read-only mode indicator */}
        <div className="view-only-banner">
          <i className="fas fa-eye"></i>
          <span>Vista de solo lectura - Este odontograma fue archivado el {dischargeEntry ? formatDate(dischargeEntry.date) : ''}</span>
        </div>

        {/* Odontogram visual */}
        <div className="odontogram-panel">
          <Odontogram 
            mode={mode} 
            selected={getAllSelections()} 
            onToggle={() => {}} // Read-only: no-op on click
            diagnosisMap={getDiagnosisMap()}
            showCompleteToothX={false}
          />
        </div>

        {/* Added selections (view only) */}
        <div className="note-and-selections">
          <div className="added-selections">
            <h3>
              <i className="fas fa-list"></i>
              Diagnósticos Registrados ({addedSelections.length})
            </h3>
            {addedSelections.length === 0 ? (
              <p className="muted">No hay diagnósticos registrados en este odontograma.</p>
            ) : (
              <div className="added-selections-list">
                {addedSelections.map((selection) => (
                  <div key={selection.id} className="added-selection-item view-only">
                    <div className="added-selection-header">
                      <div className="diagnosis-info">
                        <span className="diagnosis-name">
                          <i className="fas fa-stethoscope"></i>
                          {selection.diagnosis.name}
                        </span>
                        <span 
                          className="diagnosis-color-dot" 
                          style={{ 
                            backgroundColor: selection.diagnosis.color,
                            display: 'inline-block',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            marginLeft: '8px'
                          }}
                        ></span>
                      </div>
                    </div>
                    
                    <div className="selection-content">
                      <div className="teeth-selections">
                        {Object.entries(selection.selections).map(([toothLabel, surfaceData]) => (
                          <div key={toothLabel} className="tooth-selection">
                            <span className="tooth-label">{toothLabel}:</span>
                            <div className="surfaces">
                              {Object.keys(surfaceData).map(surface => {
                                const surfaceName = getSurfaceName(toothLabel, surface);
                                return (
                                  <span key={surface} className="surface-chip">
                                    {surfaceName}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {selection.noteText && (
                        <div className="selection-note">
                          <strong>Nota:</strong> {selection.noteText}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* General notes (if any) */}
        {odontogramData?.text && (
          <div className="general-notes-section">
            <h3>
              <i className="fas fa-file-medical-alt"></i>
              Notas Generales
            </h3>
            <div className="general-notes-display">
              <p>{odontogramData.text}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewDischargeOdontogram;
